import React, { useState } from 'react';
import {
    NEWSLETTER_CONSENT_REQUIRED,
    NEWSLETTER_CONSENT_TEXT,
    newsletterErrorMessage,
    subscribeToNewsletter,
    validateEmail,
} from './emailService';

const Newsletter = () => {
    const [email, setEmail] = useState('');
    const [consent, setConsent] = useState(false);
    const [honeypot, setHoneypot] = useState('');
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isFocused, setIsFocused] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setSuccess('');
        setIsSubmitting(true);

        if (!validateEmail(email)) {
            setError('Please enter a valid email address.');
            setIsSubmitting(false);
            return;
        }

        if (!consent) {
            setError(NEWSLETTER_CONSENT_REQUIRED);
            setIsSubmitting(false);
            return;
        }

        try {
            setSuccess(await subscribeToNewsletter(email, { consent, honeypot }));
            setEmail('');
            setConsent(false);
            setHoneypot('');
        } catch (submitError) {
            setError(newsletterErrorMessage(submitError));
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div>
            <form onSubmit={handleSubmit} noValidate>
                <fieldset disabled={isSubmitting}>
                    <legend className="sr-only">Newsletter Subscription</legend>

                    <div className="absolute left-[-10000px] h-0 w-0 overflow-hidden" aria-hidden="true">
                        <label htmlFor="newsletter-company">Company</label>
                        <input
                            id="newsletter-company"
                            type="text"
                            name="company"
                            tabIndex={-1}
                            autoComplete="off"
                            value={honeypot}
                            onChange={(e) => setHoneypot(e.target.value)}
                        />
                    </div>
                    
                    <div className='grid items-center justify-center grid-cols-1 gap-4 md:grid-cols-3'>
                        <div className='md:ml-auto md:mb-6'>
                            <p className=''>
                                <strong>Sign up for our newsletter</strong>
                            </p>
                        </div>

                        <div className='relative md:mb-6'>
                            <input
                                type='email'
                                className={`peer block min-h-auto w-full rounded border-2 bg-transparent py-[0.32rem]
                                  px-3 leading-[1.6] text-neutral-200 outline-none transition-all duration-200 ease-linear
                                  focus:ring-2 focus:ring-white focus:border-white
                                  motion-reduce:transition-none ${error ? 'border-red-500' : 'border-neutral-300'}
                                  placeholder:text-transparent focus:placeholder:text-neutral-400`}
                                id='newsletter-email'
                                name='email'
                                placeholder='Email address'
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                onFocus={() => setIsFocused(true)}
                                onBlur={() => setIsFocused(false)}
                                required
                                aria-required="true"
                                aria-invalid={error ? 'true' : 'false'}
                                aria-describedby={error ? 'email-error' : 'email-help'}
                                autoComplete="email"
                                disabled={isSubmitting}
                            />
                            <label
                                htmlFor='newsletter-email'
                                className={`pointer-events-none absolute left-3 mb-0 max-w-[90%] origin-top-left
                                  truncate leading-[1.6] text-neutral-200 transition-all duration-200
                                  ease-out ${email || isFocused 
                                    ? '-top-2 scale-[0.8] bg-neutral-900 px-1' 
                                    : 'top-[0.37rem]'
                                  } motion-reduce:transition-none`}
                            >
                                Email address <span className="text-red-400">*</span>
                            </label>
                            <div id="email-help" className="text-xs text-neutral-400 mt-1">
                                We&#39;ll never share your email with anyone else.
                            </div>
                        </div>

                        <div className='mb-6 md:mr-auto'>
                            <button
                                type='submit'
                                disabled={isSubmitting}
                                className='inline-block rounded border-2 border-neutral-50 px-6 pt-2 pb-1.5 text-xs font-medium uppercase leading-normal text-neutral-50 transition duration-150 ease-in-out hover:border-neutral-100 hover:bg-neutral-500 hover:bg-opacity-10 hover:text-neutral-100 focus:border-neutral-100 focus:text-neutral-100 focus:outline-none focus:ring-2 focus:ring-ghContrast-light-blue disabled:opacity-50 disabled:cursor-not-allowed'
                                aria-describedby={isSubmitting ? 'submit-status' : undefined}
                            >
                                {isSubmitting ? 'Subscribing...' : 'Subscribe'}
                            </button>
                            {isSubmitting && (
                                <span id="submit-status" className="sr-only">
                                    Submitting your subscription request
                                </span>
                            )}
                        </div>
                    </div>

                    <div className="mb-6 flex justify-center">
                        <label htmlFor="newsletter-consent" className="flex max-w-xl items-start gap-2 text-left text-sm text-neutral-200">
                            <input
                                id="newsletter-consent"
                                type="checkbox"
                                className="mt-1 h-4 w-4 shrink-0 accent-neutral-50"
                                checked={consent}
                                onChange={(e) => setConsent(e.target.checked)}
                                required
                                aria-required="true"
                                disabled={isSubmitting}
                            />
                            <span>{NEWSLETTER_CONSENT_TEXT} <span className="text-red-400">*</span></span>
                        </label>
                    </div>
                </fieldset>
                
                <div aria-live="polite" aria-atomic="true">
                    {error && (
                        <p id="email-error" className='text-red-400 text-sm mt-2' role="alert">
                            <span className="font-medium">Error:</span> {error}
                        </p>
                    )}
                    {success && (
                        <p className='text-green-400 text-sm mt-2' role="status">
                            <span className="font-medium">Success:</span> {success}
                        </p>
                    )}
                </div>
            </form>
        </div>
    );
};

export default Newsletter;
